import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Stethoscope, Pill, Activity, Heart, Users, MapPin, Clock, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { clinicInfo, homeContent } from '../mock';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {clinicInfo.acceptingNewPatients && (
              <div className="inline-block bg-[#006D73] text-white text-sm font-semibold px-4 py-2 rounded-full mb-6 animate-fade-in">
                Now Accepting New Patients
              </div>
            )}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#000053] mb-6 leading-tight">
              {homeContent.hero.title}
            </h1>
            <p className="text-xl text-[#36454F] mb-8 leading-relaxed">
              {homeContent.hero.subtitle}
            </p>
            <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
              {homeContent.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button size="lg" className="bg-[#006D73] text-white hover:bg-[#005a5f] transition-all transform hover:scale-105 text-lg px-8 py-6">
                  Book an Appointment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href={`tel:${clinicInfo.phone}`}>
                <Button size="lg" variant="outline" className="border-2 border-[#006D73] text-[#006D73] hover:bg-[#006D73] hover:text-white transition-all text-lg px-8 py-6">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#000053] mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive healthcare services tailored to your needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {homeContent.services.map((service, index) => (
              <Card key={service.id} className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 hover:border-[#006D73]">
                <CardHeader>
                  <div className="h-12 w-12 bg-[#006D73] bg-opacity-10 rounded-lg flex items-center justify-center mb-4">
                    {service.id === 'family-practice' && <Stethoscope className="h-6 w-6 text-[#006D73]" />}
                    {service.id === 'pharmacy' && <Pill className="h-6 w-6 text-[#006D73]" />}
                    {service.id === 'physiotherapy' && <Activity className="h-6 w-6 text-[#006D73]" />}
                    {service.id === 'chiropractic' && <Heart className="h-6 w-6 text-[#006D73]" />}
                    {service.id === 'massage' && <Users className="h-6 w-6 text-[#006D73]" />}
                    {service.id === 'orthotics' && <Activity className="h-6 w-6 text-[#006D73]" />}
                  </div>
                  <CardTitle className="text-[#000053]">{service.title}</CardTitle>
                  <CardDescription className="text-gray-600">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={service.link}>
                    <Button variant="link" className="text-[#006D73] hover:text-[#005a5f] p-0 h-auto font-semibold">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Hours Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Location */}
            <div>
              <h2 className="text-3xl font-bold text-[#000053] mb-6">Visit Us</h2>
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <MapPin className="h-6 w-6 text-[#006D73] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-[#000053] mb-2">Address</h3>
                    <p className="text-gray-600">{clinicInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 mb-6">
                  <Phone className="h-6 w-6 text-[#006D73] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-[#000053] mb-2">Phone</h3>
                    <a href={`tel:${clinicInfo.phone}`} className="text-[#006D73] hover:underline">
                      {clinicInfo.phone}
                    </a>
                  </div>
                </div>
                {/* Map */}
                <div className="rounded-lg h-64 overflow-hidden shadow-md">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2916.8!2d-81.2!3d43.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDAwJzAwLjAiTiA4McKwMTInMDAuMCJX!5e0!3m2!1sen!2sca!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Polari Health Location"
                  ></iframe>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div>
              <h2 className="text-3xl font-bold text-[#000053] mb-6">Hours of Operation</h2>
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Clock className="h-6 w-6 text-[#006D73]" />
                  <h3 className="font-semibold text-[#000053]">We're Here When You Need Us</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="font-medium text-[#36454F]">Monday</span>
                    <span className="text-gray-600">{clinicInfo.hours.monday}</span>
                  </li>
                  <li className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="font-medium text-[#36454F]">Tuesday</span>
                    <span className="text-gray-600">{clinicInfo.hours.tuesday}</span>
                  </li>
                  <li className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="font-medium text-[#36454F]">Wednesday</span>
                    <span className="text-gray-600">{clinicInfo.hours.wednesday}</span>
                  </li>
                  <li className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="font-medium text-[#36454F]">Thursday</span>
                    <span className="text-gray-600">{clinicInfo.hours.thursday}</span>
                  </li>
                  <li className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="font-medium text-[#36454F]">Friday</span>
                    <span className="text-gray-600">{clinicInfo.hours.friday}</span>
                  </li>
                  <li className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="font-medium text-[#36454F]">Saturday</span>
                    <span className="text-gray-600">{clinicInfo.hours.saturday}</span>
                  </li>
                  <li className="flex justify-between items-center py-3">
                    <span className="font-medium text-[#36454F]">Sunday</span>
                    <span className="text-gray-600">{clinicInfo.hours.sunday}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#000053]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Experience Better Healthcare?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join our clinic family and discover comprehensive, compassionate care.
            </p>
            <Link to="/book">
              <Button size="lg" className="bg-[#006D73] text-white hover:bg-[#005a5f] transition-all transform hover:scale-105 text-lg px-8 py-6">
                Book Your Appointment Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;